'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { Database } from '@/types/supabase';
import { toast } from 'sonner';

// Types
export interface VendorLink {
  id: string;
  vendor_id: string;
  link_code: string;
  custom_link_code: string | null;
  show_prices: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface VendorInfo {
  id: string;
  company_name: string | null;
  description: string | null;
  mobile_number: string | null;
  address: string | null;
  logo_url: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  website_url: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface VendorProduct {
  id: string;
  name: string;
  description: string | null;
  vendor_price: number | null;
  food_course: string | null;
  subcategory_id: string | null;
  image_url: string | null;
  is_vegetarian: boolean | null;
  contains_egg: boolean | null;
  not_a_food: boolean | null;
  min_order: number;
  max_order: number;
  short_code: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface VendorValidationResponse {
  vendor_link: VendorLink;
  vendor: VendorInfo;
  is_valid: boolean;
}

export interface VendorProductsResponse {
  products: VendorProduct[];
  combos: unknown[]; // Added combos field
  grouped_products: Record<string, VendorProduct[]>;
  total_count: number;
  combos_count: number; // Added combos count
  vendor_id: string;
}

export interface VendorCourse {
  id: string;
  name: string;
  priority: number;
  created_at: string | null;
  updated_at: string | null;
  vendor_id?: string; // Optional for public API compatibility
}

export interface VendorCoursesResponse {
  courses: VendorCourse[];
  vendor_id: string;
  total_count: number;
}

export interface VendorSubcategory {
  id: string;
  name: string;
  course_id: string;
  priority: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface VendorSubcategoriesResponse {
  subcategories: VendorSubcategory[];
  vendor_id: string;
  total_count: number;
}

export interface CreateVendorLinkData {
  link_code?: string;
  show_prices?: boolean;
}

export interface UpdateVendorLinkData {
  custom_link_code?: string;
  show_prices?: boolean;
}

// API client functions
const menuWebsiteApi = {
  // Get vendor link for authenticated user
  async getVendorLink(): Promise<{ data: VendorLink | null; message: string }> {
    const supabase = createClientComponentClient<Database>();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required');
    }

    const response = await fetch('/api/menu/links', {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vendor link' }));
      throw new Error(error.message || 'Failed to fetch vendor link');
    }

    return response.json();
  },

  // Create vendor link
  async createVendorLink(data: CreateVendorLinkData): Promise<{ data: VendorLink; message: string }> {
    const supabase = createClientComponentClient<Database>();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required');
    }

    const response = await fetch('/api/menu/links', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create vendor link' }));
      throw new Error(error.message || 'Failed to create vendor link');
    }

    return response.json();
  },

  // Update vendor link
  async updateVendorLink(data: UpdateVendorLinkData): Promise<{ data: VendorLink; message: string }> {
    const supabase = createClientComponentClient<Database>();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required');
    }

    const response = await fetch('/api/menu/links', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update vendor link' }));
      throw new Error(error.message || 'Failed to update vendor link');
    }

    return response.json();
  },

  // Delete vendor link
  async deleteVendorLink(): Promise<{ data: null; message: string }> {
    const supabase = createClientComponentClient<Database>();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required');
    }

    const response = await fetch('/api/menu/links', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete vendor link' }));
      throw new Error(error.message || 'Failed to delete vendor link');
    }

    return response.json();
  },

  // Validate vendor link code (public endpoint)
  async validateVendorLink(code: string): Promise<{ data: VendorValidationResponse; message: string }> {
    const response = await fetch(`/api/menu/validate/${code}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to validate vendor link' }));
      throw new Error(error.message || 'Failed to validate vendor link');
    }

    return response.json();
  },

  // Get vendor info by ID (public endpoint)
  async getVendorInfo(vendorId: string): Promise<{ data: VendorInfo; message: string }> {
    const response = await fetch(`/api/menu/${vendorId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vendor info' }));
      throw new Error(error.message || 'Failed to fetch vendor info');
    }

    return response.json();
  },

  // Get vendor products (public endpoint)
  async getVendorProducts(vendorId: string, options?: {
    course?: string;
    search?: string;
  }): Promise<{ data: VendorProductsResponse; message: string }> {
    const searchParams = new URLSearchParams();
    if (options?.course) searchParams.set('course', options.course);
    if (options?.search) searchParams.set('search', options.search);

    const url = `/api/menu/${vendorId}/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vendor products' }));
      throw new Error(error.message || 'Failed to fetch vendor products');
    }

    return response.json();
  },

  // Get vendor courses (public endpoint)
  async getVendorCourses(vendorId: string): Promise<{ data: VendorCoursesResponse; message: string }> {
    const response = await fetch(`/api/menu/${vendorId}/courses`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vendor courses' }));
      throw new Error(error.message || 'Failed to fetch vendor courses');
    }

    return response.json();
  },

  // Get vendor subcategories (public endpoint)
  async getVendorSubcategories(vendorId: string): Promise<{ data: VendorSubcategoriesResponse; message: string }> {
    const response = await fetch(`/api/menu/${vendorId}/subcategories`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch vendor subcategories' }));
      throw new Error(error.message || 'Failed to fetch vendor subcategories');
    }

    return response.json();
  },
};

// React Query hooks

// Query for vendor link (authenticated)
export function useVendorLink() {
  return useQuery({
    queryKey: ['vendorLink'],
    queryFn: () => menuWebsiteApi.getVendorLink(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Query for vendor link validation (public)
export function useValidateVendorLink(code: string) {
  return useQuery({
    queryKey: ['validateVendorLink', code],
    queryFn: () => menuWebsiteApi.validateVendorLink(code),
    enabled: !!code,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1, // Only retry once for validation
  });
}

// Query for vendor info (public)
export function useVendorInfo(vendorId: string) {
  return useQuery({
    queryKey: ['vendorInfo', vendorId],
    queryFn: () => menuWebsiteApi.getVendorInfo(vendorId),
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Query for vendor products (public)
export function useVendorProducts(vendorId: string, options?: {
  course?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ['vendorProducts', vendorId, options],
    queryFn: () => menuWebsiteApi.getVendorProducts(vendorId, options),
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Query for vendor courses (public)
export function useVendorCourses(vendorId: string) {
  return useQuery({
    queryKey: ['vendorCourses', vendorId],
    queryFn: () => menuWebsiteApi.getVendorCourses(vendorId),
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1, // Only retry once for public endpoint
  });
}

// Query for vendor subcategories (public)
export function useVendorSubcategories(vendorId: string) {
  return useQuery({
    queryKey: ['vendorSubcategories', vendorId],
    queryFn: () => menuWebsiteApi.getVendorSubcategories(vendorId),
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1, // Only retry once for public endpoint
  });
}

// Mutations

// Mutation for creating vendor link
export function useCreateVendorLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: menuWebsiteApi.createVendorLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorLink'] });
      toast.success('Vendor link created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create vendor link');
    },
  });
}

// Mutation for updating vendor link
export function useUpdateVendorLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: menuWebsiteApi.updateVendorLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorLink'] });
      toast.success('Vendor link updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update vendor link');
    },
  });
}

// Mutation for deleting vendor link
export function useDeleteVendorLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: menuWebsiteApi.deleteVendorLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorLink'] });
      toast.success('Vendor link deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete vendor link');
    },
  });
}

// Utility hooks for optimistic updates and cache management

// Prefetch vendor link
export function usePrefetchVendorLink() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: ['vendorLink'],
      queryFn: () => menuWebsiteApi.getVendorLink(),
      staleTime: 1000 * 60 * 5,
    });
  };
}

// Get cached vendor link data
export function useVendorLinkCache() {
  const queryClient = useQueryClient();
  
  return queryClient.getQueryData(['vendorLink']);
}